import type React from 'react';
import { useCallback, useState } from 'react';
import SecurityDepositPage from './modules/SecurityDepositPage';
import ProratedRentPage from './modules/ProratedRentPage';
import OtherPage from './modules/OtherPage';
import DueDatePage from './modules/DueDatePage';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

interface Props {
  saveStateToReduxStore: (payload: any) => Promise<any>;
  onCancel: () => void;
  onSubmit: (editMode: boolean) => Record<string, any>;
  onClose: () => void;
  onBack: () => void;
  setupNewTerm: boolean;
  goToConfirmation: () => void;
  showModalNotification: (args: string) => Record<string, any>;
  goToExperianFlow: () => void;
  securityDepositMandatesFromLease: Array<Record<string, string>>;
  securityDeposit: number;
  securityDepositPaymentAmount: number;
  securityDepositNotes: string;
  securityDepositStartsOpen: boolean;
  proratedRentFromLease: number;
  proratedRentPaymentAmount: number;
  proratedRentNotes: string;
  proratedRentStartsOpen: boolean;
  otherMicFromLease: Array<Record<string, any>>;
  otherSettings: Array<{ otherPaymentAmount: number; otherFeeType: string; otherNotes: string }>;
  otherTotalPaymentAmount: number;
  moveInCostsPaymentLimits: number;
  totalPaymentAmount: number;
  dueDate: string;
  editMode: boolean;
  fromDashboard: boolean;
  individualLimits: Record<string, number>;
  currentProperty: ZrmClickstreamListing;
  user: ZrmClickstreamUser;
}

const SECURITY_DEPOSIT_PAGE = 1;
const PRORATED_PAGE = 2;
const OTHER_PAGE = 3;
const DUE_DATE_PAGE = 4;

function getPageNameMap(currentPage: number): string {
  const pageNameMap = {
    [SECURITY_DEPOSIT_PAGE]: 'move_in_costs | security_deposit',
    [PRORATED_PAGE]: 'move_in_costs | prorated_rent',
    [OTHER_PAGE]: 'move_in_costs | other_move_in_costs',
    [DUE_DATE_PAGE]: 'move_in_date',
  };
  return pageNameMap[currentPage];
}

const MoveInCostsPage: React.FC<Props> = ({
  saveStateToReduxStore: propsSaveStateToReduxStore,
  onCancel,
  onSubmit,
  onClose: propsOnClose,
  onBack,
  setupNewTerm = false,
  goToConfirmation,
  showModalNotification,
  goToExperianFlow,
  securityDepositMandatesFromLease = [],
  securityDeposit = 0,
  securityDepositPaymentAmount = 0,
  securityDepositNotes = '',
  securityDepositStartsOpen = false,
  proratedRentFromLease = 0,
  proratedRentPaymentAmount = 0,
  proratedRentNotes = '',
  proratedRentStartsOpen = false,
  otherMicFromLease = [],
  otherSettings = [],
  otherTotalPaymentAmount,
  moveInCostsPaymentLimits,
  totalPaymentAmount,
  dueDate,
  editMode = false,
  fromDashboard = false,
  individualLimits = {
    deposit: 0,
    proratedRent: 0,
    other: 0,
  },
  currentProperty,
  user,
}) => {
  const [currentPage, setCurrentPage] = useState(SECURITY_DEPOSIT_PAGE);
  const [pageVisited, setPageVisited] = useState(new Set<number>());

  const getPreviousPage = useCallback((currentPage: number): number => currentPage - 1, []);

  const getNextPage = useCallback((currentPage: number): number => currentPage + 1, []);

  const onClose = useCallback((): void => {
    if (!fromDashboard) {
      IreneOnboardingCSEvents.emitExitFromOnboardingFlowEvent({
        currentProperty,
        user,
        triggerObject: getPageNameMap(currentPage),
      });
    }
    propsOnClose();
  }, [fromDashboard, currentProperty, user, currentPage, propsOnClose]);

  const togglePrevPage = useCallback((): void => {
    IreneOnboardingCSEvents.emitBackFromOnboardingFlowEvent({
      currentProperty,
      user,
      triggerObject: getPageNameMap(currentPage),
    });

    if (currentPage === SECURITY_DEPOSIT_PAGE) {
      onBack();
    } else {
      setCurrentPage(getPreviousPage(currentPage));
    }
  }, [currentProperty, user, currentPage, onBack, getPreviousPage]);

  const toggleNextPage = useCallback((): void => {
    setCurrentPage(getNextPage(currentPage));
    setPageVisited((prev) => new Set(prev).add(currentPage));
  }, [currentPage, getNextPage]);

  const saveStateToReduxStore = useCallback(
    async (payload): Promise<any> => {
      const result = await propsSaveStateToReduxStore(payload);

      if (result && result.success) {
        // Check if we should skip due date page, considering the new payload data
        const shouldSkipDueDate =
          currentPage === OTHER_PAGE &&
          securityDepositStartsOpen === false &&
          proratedRentStartsOpen === false &&
          (!payload.otherSettings || payload.otherSettings.length === 0);

        if (shouldSkipDueDate) {
          if (setupNewTerm) {
            goToConfirmation();
          } else {
            onSubmit(editMode);
          }
        } else {
          toggleNextPage();
        }
      }
    },
    [
      propsSaveStateToReduxStore,
      currentPage,
      securityDepositStartsOpen,
      proratedRentStartsOpen,
      setupNewTerm,
      goToConfirmation,
      onSubmit,
      editMode,
      toggleNextPage,
    ],
  );

  const renderCurrentPage = (): React.ReactNode => {
    if (currentPage === SECURITY_DEPOSIT_PAGE) {
      return (
        <SecurityDepositPage
          saveStateToReduxStore={saveStateToReduxStore}
          showModalNotification={showModalNotification}
          securityDepositPaymentAmount={securityDepositPaymentAmount}
          securityDepositNotes={securityDepositNotes}
          securityDepositStartsOpen={securityDepositStartsOpen}
          securityDepositMandatesFromLease={securityDepositMandatesFromLease}
          moveInCostsPaymentLimits={moveInCostsPaymentLimits}
          individualLimit={individualLimits.deposit}
          totalPaymentAmount={totalPaymentAmount}
          editMode={editMode}
          fromDashboard={fromDashboard}
          securityDeposit={securityDeposit}
          pageVisited={pageVisited}
          currentProperty={currentProperty}
          user={user}
          onClose={onClose}
          currentPage={currentPage}
          setupNewTerm={setupNewTerm}
          togglePrevPage={togglePrevPage}
        />
      );
    } else if (currentPage === PRORATED_PAGE) {
      return (
        <ProratedRentPage
          saveStateToReduxStore={saveStateToReduxStore}
          showModalNotification={showModalNotification}
          proratedRentPaymentAmount={proratedRentPaymentAmount}
          proratedRentNotes={proratedRentNotes}
          proratedRentStartsOpen={proratedRentStartsOpen}
          moveInCostsPaymentLimits={moveInCostsPaymentLimits}
          individualLimit={individualLimits.proratedRent}
          totalPaymentAmount={totalPaymentAmount}
          editMode={editMode}
          fromDashboard={fromDashboard}
          proratedRentFromLease={proratedRentFromLease}
          pageVisited={pageVisited}
          currentProperty={currentProperty}
          user={user}
          onClose={onClose}
          togglePrevPage={togglePrevPage}
        />
      );
    } else if (currentPage === OTHER_PAGE) {
      return (
        <OtherPage
          saveStateToReduxStore={saveStateToReduxStore}
          showModalNotification={showModalNotification}
          otherSettings={otherSettings}
          otherTotalPaymentAmount={otherTotalPaymentAmount}
          moveInCostsPaymentLimits={moveInCostsPaymentLimits}
          individualLimit={individualLimits.other}
          totalPaymentAmount={totalPaymentAmount}
          editMode={editMode}
          fromDashboard={fromDashboard}
          otherMicFromLease={otherMicFromLease}
          pageVisited={pageVisited}
          currentProperty={currentProperty}
          user={user}
          onClose={onClose}
          togglePrevPage={togglePrevPage}
        />
      );
    } else if (currentPage === DUE_DATE_PAGE) {
      return (
        <DueDatePage
          saveStateToReduxStore={saveStateToReduxStore}
          onCancel={onCancel}
          dueDate={dueDate}
          editMode={editMode}
          onSubmit={onSubmit}
          fromDashboard={fromDashboard}
          setupNewTerm={setupNewTerm}
          goToExperianFlow={goToExperianFlow}
          goToConfirmation={goToConfirmation}
          currentProperty={currentProperty}
          user={user}
          onClose={onClose}
          togglePrevPage={togglePrevPage}
        />
      );
    }
    return null;
  };

  return renderCurrentPage();
};

export default MoveInCostsPage;
